/**
 * Cart quantity rules
 *
 * Handles two independent "you can't just add whatever quantity you want"
 * rules for this store, both enforced site-wide (every page, every add-to-
 * cart path), with a companion JS-independent safety net in the cart's own
 * Liquid templates so a shopper is never left staring at a checkout that
 * silently fails with no explanation.
 *
 * RULE 1 - ONE TESTER PER SKU PER ORDER
 * Adds a "tester" checkbox to the product form (rendered by
 * snippets/buy-buttons.liquid). When checked, the referenced tester variant
 * (from the product's custom.b2b_tester_product / custom.retail_tester_product
 * metafield) is added to the cart as its own line item alongside the main
 * product - see ensureTesterInCart() and the fetch-wrapping below for why
 * that add has to be sequenced carefully against this cart's PAGE (not
 * drawer) checkout redirect.
 *
 * There are two ways a shopper can end up with more than one of the same
 * tester in their cart: re-submitting the checkbox flow (handled by
 * ensureTesterInCart, which checks the live cart before adding), or adding/
 * bumping a tester from its own product page or the cart's own quantity
 * stepper - neither of which goes through the checkbox at all. That's why
 * enforceQuantityRules() below re-checks the live cart after every add/
 * change/update and once on page load, and clamps any tester line back to
 * quantity 1 no matter how it got there.
 *
 * RULE 2 - CP5-TAGGED PRODUCTS ONLY SELL IN PACKS OF 5
 * Any product tagged "CP5" in the admin can only be purchased in multiples
 * of 5 (5, 10, 15, ...). snippets/buy-buttons.liquid stamps a hidden
 * `properties[_cp5]` value onto every line added for a CP5 product (cart.js
 * item JSON doesn't include product tags, but it does include line item
 * properties, so this is how this script recognizes a CP5 line without a
 * separate lookup). The cart page's own quantity stepper (sections/main-
 * cart-items.liquid) also sets a minimum/step of 5 for these lines directly,
 * but the underlying number input can still be typed into or land on a
 * non-multiple via the stepper's native browser behavior, so
 * enforceQuantityRules() rounds any CP5 line up to the nearest multiple of 5
 * (minimum 5) after every cart mutation and on page load, the same way it
 * clamps testers.
 *
 * RULE 3 - "1Max"-TAGGED PRODUCTS ARE LIMITED TO 1 PER ORDER
 * Any product tagged "1Max" in the admin (merchandising displays, signs,
 * sample packs, and - since testers are always tagged 1Max too - every
 * tester product's own page) can only have one in the cart at a time, same
 * idea as the tester cap above but driven by a tag instead of a title
 * pattern. Uses the same hidden-property-stamp trick as CP5 (`properties
 * [_max1]`, stamped by snippets/buy-buttons.liquid) since cart.js item JSON
 * doesn't carry tags. Kept as its own check alongside the tester-title check
 * rather than replacing it, so nothing changes for testers even if a tag
 * were ever removed by mistake.
 *
 * A companion, JS-independent safety net lives in the Liquid templates
 * (sections/main-cart-items.liquid, sections/main-cart-footer.liquid): they
 * server-render clear messages and disable the Checkout button (and hide
 * Shop Pay / other express-checkout buttons) whenever a tester or 1Max line
 * is over quantity 1, a CP5 line isn't a multiple of 5, or the cart is under
 * the merchant-configured order minimum (Theme settings > Cart > Order
 * minimum).
 */
(function () {
  if (window.__cartQuantityRulesLoaded) return;
  window.__cartQuantityRulesLoaded = true;

  // Captured before window.fetch is wrapped below, and used for every request
  // THIS FILE makes on its own (fetchCart, postCart). That's deliberate: if our
  // own correction/lookup requests went through the wrapped window.fetch further
  // down, each one would recursively re-trigger this file's own cart-mutation
  // handling again, compounding into a chain of nested round-trips (and, if a
  // shopper clicked quickly, overlapping chains) that could take a very long
  // time to settle - the cart's loading spinner spins until every nested layer
  // finishes, which can look like it's stuck. Going straight to the native
  // fetch for our own requests keeps this file's own network calls flat: one
  // request in, one response out, never re-entering this wrapper.
  var nativeFetch = window.fetch.bind(window);

  var TESTER_TITLE_PATTERN = /^tester,/i;
  var CP5_PROPERTY_KEY = '_cp5';
  var CP5_MULTIPLE = 5;
  var MAX1_PROPERTY_KEY = '_max1';

  var handledThisLoad = Object.create(null);
  var pendingTesterVariantId = null;

  function isTesterTitle(title) {
    return typeof title === 'string' && TESTER_TITLE_PATTERN.test(title.trim());
  }

  function isCp5Item(item) {
    return !!(item && item.properties && item.properties[CP5_PROPERTY_KEY]);
  }

  function isMax1Item(item) {
    return !!(item && item.properties && item.properties[MAX1_PROPERTY_KEY]);
  }

  function roundUpToMultiple(quantity, multiple) {
    return Math.ceil(quantity / multiple) * multiple;
  }

  function fetchCart() {
    return nativeFetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .catch(function () {
        return { items: [] };
      });
  }

  var cartSnapshotPromise = fetchCart();

  function getCartElement() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function postCart(url, variantId, quantity, cart) {
    var body;
    var headers;

    if (cart && typeof cart.getSectionsToRender === 'function') {
      var sectionIds = cart.getSectionsToRender().map(function (section) {
        return section.id;
      });
      var params = new URLSearchParams();
      params.append('id', variantId);
      params.append('quantity', String(quantity));
      params.append('sections', sectionIds.join(','));
      params.append('sections_url', window.location.pathname);
      body = params.toString();
      headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
      };
    } else {
      // Shopify's /cart/change.js validates "id" against a string pattern
      // (either a bare numeric string or a "variant_id:properties_hash" key)
      // and rejects a plain JSON number with a 400 ("expected Integer to be
      // a ..."). The form-urlencoded branch above never hits this because
      // URLSearchParams always serializes values to strings - only this
      // JSON branch (used whenever there's no cart-notification/cart-drawer
      // element, i.e. this store's "page" cart type) needs the explicit
      // String() coercion.
      body = JSON.stringify({ id: String(variantId), quantity: quantity });
      headers = {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
      };
    }

    return nativeFetch(url, {
      method: 'POST',
      headers: headers,
      body: body,
      keepalive: true,
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (response) {
        // Shopify uses a couple of different shapes for a failed cart
        // request: a sold-out/quantity-rule violation comes back as
        // {status, message, description}, but a malformed-request error
        // (like the id-type issue this comment used to describe) comes
        // back as just {errors: "..."} with no top-level "status" - check
        // for both so a failure here is never silently swallowed.
        if (response.status || response.errors) {
          console.error(
            'Cart quantity rules: could not update cart',
            response.message || response.description || response.errors
          );
          return;
        }
        if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(response);
        }
      })
      .catch(function (err) {
        console.error('Cart quantity rules: request failed', err);
      });
  }

  function ensureTesterInCart(testerVariantId, cart) {
    if (handledThisLoad[testerVariantId]) return Promise.resolve();
    handledThisLoad[testerVariantId] = true;

    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
    var changeUrl = (window.routes && window.routes.cart_change_url) || '/cart/change.js';

    return cartSnapshotPromise.then(function (snapshot) {
      var items = (snapshot && snapshot.items) || [];
      var existing = null;
      for (var i = 0; i < items.length; i++) {
        if (String(items[i].variant_id) === String(testerVariantId)) {
          existing = items[i];
          break;
        }
      }

      if (!existing) {
        return postCart(addUrl, testerVariantId, 1, cart);
      }
      if (existing.quantity > 1) {
        return postCart(changeUrl, testerVariantId, 1, cart);
      }
      // Already in the cart at quantity 1 - nothing to do.
    });
  }

  // Site-wide safety net for BOTH quantity rules: whatever path a line got
  // into the cart through (a checkbox, a product's own page, a collection
  // quick-add, the cart page's own quantity stepper), correct it. Runs after
  // every cart mutation and once on initial page load.
  function enforceQuantityRules(cart) {
    var changeUrl = (window.routes && window.routes.cart_change_url) || '/cart/change.js';

    return fetchCart().then(function (currentCart) {
      var items = (currentCart && currentCart.items) || [];
      var corrections = [];

      items.forEach(function (item) {
        if ((isTesterTitle(item.product_title) || isMax1Item(item)) && item.quantity > 1) {
          corrections.push({ variantId: item.variant_id, quantity: 1 });
          return;
        }
        if (isCp5Item(item) && item.quantity % CP5_MULTIPLE !== 0) {
          corrections.push({ variantId: item.variant_id, quantity: roundUpToMultiple(item.quantity, CP5_MULTIPLE) });
        }
      });

      if (corrections.length === 0) return false;

      // Apply sequentially so we don't race multiple /cart/change.js calls
      // against each other (each targets a line by variant id, so concurrent
      // calls could clobber one another's server-side cart state).
      return corrections
        .reduce(function (chain, correction) {
          return chain.then(function () {
            return postCart(changeUrl, correction.variantId, correction.quantity, cart);
          });
        }, Promise.resolve())
        .then(function () {
          return true;
        });
    });
  }

  // With cart_type set to "drawer" (or "notification"), our own extra cart
  // mutations after the theme's own add request (a tester add, or a CP5/1Max
  // correction) each already render the drawer correctly via postCart's own
  // cart.renderContents() call above - but the theme's product-form.js then
  // runs its OWN .then(response => response.json()).then(this.cart.
  // renderContents(response)) using the response object from the ORIGINAL
  // add request, which is now stale (missing the tester line, or showing a
  // pre-correction quantity), silently overwriting our correct render. This
  // fetches a fresh, side-effect-free snapshot of the cart's rendered
  // sections (no "updates" param, so nothing changes server-side) so that
  // snapshot can be substituted for the stale response before handing it
  // back to product-form.js.
  function refreshCartSections(cart) {
    var updateUrl = (window.routes && window.routes.cart_update_url) || '/cart/update.js';
    var sectionIds =
      cart && typeof cart.getSectionsToRender === 'function'
        ? cart.getSectionsToRender().map(function (section) {
            return section.id;
          })
        : [];

    var params = new URLSearchParams();
    params.append('sections', sectionIds.join(','));
    params.append('sections_url', window.location.pathname);

    return nativeFetch(updateUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
      },
      body: params.toString(),
    })
      .then(function (response) {
        return response.json();
      })
      .catch(function () {
        return null;
      });
  }

  function requestUrlAndMethod(input, init) {
    if (typeof input === 'string') {
      return { url: input, method: (init && init.method) || 'GET' };
    }
    if (input && typeof input === 'object') {
      return { url: input.url || '', method: input.method || (init && init.method) || 'GET' };
    }
    return { url: '', method: 'GET' };
  }

  function isCartMutationUrl(url) {
    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
    var changeUrl = (window.routes && window.routes.cart_change_url) || '/cart/change.js';
    var updateUrl = (window.routes && window.routes.cart_update_url) || '/cart/update.js';
    return url.indexOf(addUrl) !== -1 || url.indexOf(changeUrl) !== -1 || url.indexOf(updateUrl) !== -1;
  }

  // Wrap window.fetch so we can (a) delay the theme's own add-to-cart
  // continuation until our own tester-add has landed (see file-level
  // comment), and (b) - regardless of whether this particular request came
  // from the tester checkbox at all - re-check the live cart afterwards and
  // correct any tester or CP5 line. (b) is what makes this a site-wide fix
  // rather than one scoped to a single form. Everything this wrapper itself
  // triggers (ensureTesterInCart, enforceQuantityRules) goes through
  // nativeFetch above, not this wrapped fetch, so none of that follow-up work
  // ever re-enters this function.
  window.fetch = function (input, init) {
    var req = requestUrlAndMethod(input, init);
    var isMutation = req.method.toUpperCase() === 'POST' && isCartMutationUrl(req.url);

    if (!isMutation) {
      return nativeFetch(input, init);
    }

    var testerVariantId = pendingTesterVariantId;
    pendingTesterVariantId = null; // consume so later, unrelated fetch calls pass straight through

    return nativeFetch(input, init).then(function (response) {
      var clone = response.clone();
      return clone
        .json()
        .then(function (data) {
          if (data && data.status) {
            // The mutation itself failed (sold out, etc.) - don't add the tester
            // and don't bother re-checking quantities; just hand the untouched
            // response back for the theme to handle.
            return response;
          }
          var hadTesterAdd = !!testerVariantId;
          var afterMainAdd = testerVariantId
            ? ensureTesterInCart(testerVariantId, getCartElement())
            : Promise.resolve();
          return afterMainAdd
            .then(function () {
              return enforceQuantityRules(getCartElement());
            })
            .then(function (didCorrect) {
              // Either of our own extra mutations (a tester add, or a CP5/
              // 1Max correction) just changed server-side cart state AFTER
              // the theme's own request already captured its response (the
              // one we're about to hand back below) - so the theme is about
              // to render data that's already stale.
              var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
              var onCartPage = window.location.pathname.indexOf(cartUrl) !== -1;
              var extraMutationHappened = hadTesterAdd || didCorrect;

              if (didCorrect && onCartPage) {
                // The on-page cart items table has no way to know we
                // changed something behind its back, and a sections-only
                // refresh (below) wouldn't update it anyway - reload so
                // what the shopper sees matches what's actually in the
                // cart. Takes priority over the drawer refresh below.
                window.location.reload();
                return response;
              }

              var cartEl = getCartElement();
              if (extraMutationHappened && cartEl) {
                // A drawer/notification is present - substitute a fresh,
                // fully up-to-date response so the theme's own subsequent
                // renderContents() call (in product-form.js) shows the
                // FINAL state instead of overwriting our correct render
                // with the original request's now-stale data.
                return refreshCartSections(cartEl).then(function (freshData) {
                  if (!freshData) return response;
                  return new Response(JSON.stringify(freshData), {
                    status: response.status,
                    statusText: response.statusText,
                    headers: response.headers,
                  });
                });
              }

              return response;
            });
        })
        .catch(function () {
          return response;
        });
    });
  };

  document.addEventListener(
    'submit',
    function (evt) {
      var form = evt.target;
      if (!form || !form.matches || form.getAttribute('data-type') !== 'add-to-cart-form') return;

      var wrapper = form.closest('product-form');
      if (!wrapper) return;

      var checkbox = wrapper.querySelector('[data-tester-addon]');
      if (!checkbox || !checkbox.checked) return;

      var testerVariantId = checkbox.getAttribute('data-tester-variant-id');
      if (!testerVariantId) return;

      // Note the pending tester for the wrapped fetch above to pick up once the
      // theme's own add-to-cart request resolves - we deliberately don't fire our
      // own request here, so we never race the theme's redirect to /cart.
      pendingTesterVariantId = testerVariantId;
      checkbox.checked = false;
    },
    true
  );

  // Also run once on page load, so a tester or CP5 quantity that's already
  // out of bounds when the page renders (visiting /cart directly, back/
  // forward navigation, etc.) gets corrected immediately rather than waiting
  // for the next cart mutation. If a correction actually happens while
  // sitting on the cart page itself, reload so the on-screen quantities/line
  // totals reflect the corrected cart (the cart page's own JS has no way to
  // know we changed something behind its back).
  enforceQuantityRules(getCartElement()).then(function (didCorrect) {
    if (!didCorrect) return;
    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    if (window.location.pathname.indexOf(cartUrl) !== -1) {
      window.location.reload();
    }
  });
})();
